import { useState, useEffect } from 'react';
import {
  AppointmentType,
  ExpandableFieldItem,
  SchedulingData,
  useUpdateEncounterTypeMutation,
} from '@features/appointment-types';
import {
  Appointment as AppointmentResponse,
  ScheduledActionResponse,
} from '@features/appointments';
import { formatAppointmentDuration } from '../../../utils/durationUtils';
import {
  findScheduledActionByRuleId,
  mapScheduledActionStatus,
  formatActionTimestamp,
} from '../utils/scheduledActionUtils';

export interface UseFormsAndRemindersProps {
  initialAppointment?: AppointmentResponse;
  templateId?: string | null;
  onMarkDirty?: (field: 'forms' | 'reminders') => void;
  shouldPopulateDefaults?: boolean; // Allow external control of when to populate defaults
}

export const useFormsAndReminders = ({
  initialAppointment,
  templateId,
  onMarkDirty,
  shouldPopulateDefaults = true,
}: UseFormsAndRemindersProps = {}) => {
  const [updateEncounterType] = useUpdateEncounterTypeMutation();

  const initialAppointmentType =
    initialAppointment?.encounter_type && initialAppointment.encounter_type_id
      ? {
          encounter_type_id: initialAppointment.encounter_type_id,
          encounter_type: initialAppointment.encounter_type,
          provider_id: initialAppointment.provider_id || '',
          color: initialAppointment.color || 'color1', // Default to color1 if not provided
        }
      : undefined;

  // State
  const [selectedForms, setSelectedForms] = useState<ExpandableFieldItem[]>([]);
  const [selectedReminders, setSelectedReminders] = useState<ExpandableFieldItem[]>([]);
  const [selectedAppointmentType, setSelectedAppointmentType] = useState<
    AppointmentType | undefined
  >(initialAppointmentType);

  // Form handlers
  const handleAddForm = (formData: SchedulingData) => {
    const newForm: ExpandableFieldItem = {
      id: Date.now().toString(),
      name: formData.name,
      timing: formData.timing,
      offset: formData.customTiming || '',
      formId: formData.formId,
      status: 'configured',
    };
    setSelectedForms(prev => [...prev, newForm]);
    onMarkDirty?.('forms');
  };

  const handleUpdateForm = (formId: string | number, formData: SchedulingData) => {
    setSelectedForms(prev =>
      prev.map(form =>
        form.id === formId
          ? {
              ...form,
              name: formData.name,
              timing: formData.timing,
              offset: formData.customTiming || '',
              formId: formData.formId,
            }
          : form,
      ),
    );
    onMarkDirty?.('forms');
  };

  const handleRemoveForm = (formId: string | number) => {
    setSelectedForms(prev => prev.filter(form => form.id !== formId));
    onMarkDirty?.('forms');
  };

  // Reminder handlers
  const handleAddReminder = (reminderData: SchedulingData) => {
    const newReminder: ExpandableFieldItem = {
      id: Date.now().toString(),
      name: reminderData.name,
      timing: reminderData.timing,
      offset: reminderData.customTiming || '',
      status: 'configured',
    };
    setSelectedReminders(prev => [...prev, newReminder]);
    onMarkDirty?.('reminders');
  };

  const handleUpdateReminder = (reminderId: string | number, reminderData: SchedulingData) => {
    setSelectedReminders(prev =>
      prev.map(reminder =>
        reminder.id === reminderId
          ? {
              ...reminder,
              name: reminderData.name,
              timing: reminderData.timing,
              offset: reminderData.customTiming || '',
            }
          : reminder,
      ),
    );
    onMarkDirty?.('reminders');
  };

  const handleRemoveReminder = (reminderId: string | number) => {
    setSelectedReminders(prev => prev.filter(reminder => reminder.id !== reminderId));
    onMarkDirty?.('reminders');
  };

  // Appointment type handler
  const handleAppointmentTypeSelect = (appointmentType: AppointmentType | undefined) => {
    setSelectedAppointmentType(appointmentType);
  };

  // Helper function to populate forms from appointment type defaults
  const populateFormsFromDefaults = (appointmentType: AppointmentType) => {
    if (appointmentType.default_form_rules) {
      const formItems: ExpandableFieldItem[] = appointmentType.default_form_rules.map(
        (rule, index) => {
          const offset = rule.offset || '';
          let timing: string;
          try {
            timing = formatAppointmentDuration(offset);
          } catch (error) {
            console.warn(`Invalid offset format for rule ${index}:`, offset);
            timing = offset || 'at appointment time';
          }

          return {
            id: `form-${index}`,
            name: rule.title || rule.form_definition_id || `Form ${index + 1}`,
            timing: timing,
            offset: offset,
            formId: rule.form_definition_id,
            status: 'configured',
          };
        },
      );
      setSelectedForms(formItems);
    } else {
      setSelectedForms([]);
    }
  };

  // Helper function to populate reminders from appointment type defaults
  const populateRemindersFromDefaults = (appointmentType: AppointmentType) => {
    if (appointmentType.default_reminder_rules) {
      const reminderItems: ExpandableFieldItem[] = appointmentType.default_reminder_rules.map(
        (rule, index) => {
          const offset = rule.offset || '';
          let timing: string;
          try {
            timing = formatAppointmentDuration(offset);
          } catch (error) {
            console.warn(`Invalid offset format for reminder ${index}:`, offset);
            timing = offset || 'at appointment time';
          }

          return {
            id: `reminder-${index}`,
            name: rule.title || rule.reminder_template_id || `Reminder ${index + 1}`,
            timing: timing,
            offset: offset,
            status: 'configured',
          };
        },
      );
      setSelectedReminders(reminderItems);
    } else {
      setSelectedReminders([]);
    }
  };

  // Handle appointment type updates
  const handleUpdateAppointmentType = async (
    selectedDuration?: number,
    selectedTelehealth?: boolean,
  ) => {
    if (!selectedAppointmentType) return;

    try {
      const formRules = selectedForms.map(form => {
        if (!form.formId) {
          throw new Error(`Form "${form.name}" is missing formId - cannot update appointment type`);
        }
        return {
          form_definition_id: form.formId,
          offset: form.offset || '',
        };
      });

      const reminderRules = selectedReminders.map(reminder => ({
        reminder_template_id: reminder.name,
        offset: reminder.offset || '',
      }));

      const updatedAppointmentType = await updateEncounterType({
        encounterTypeId: selectedAppointmentType.encounter_type_id,
        encounterTypeUpdate: {
          encounter_type: selectedAppointmentType.encounter_type,
          default_duration: selectedDuration,
          telehealth: selectedTelehealth,
          default_form_rules: formRules.length > 0 ? formRules : null,
          default_reminder_rules: reminderRules.length > 0 ? reminderRules : null,
          template_id: templateId,
        },
      }).unwrap();

      setSelectedAppointmentType(updatedAppointmentType);
      return updatedAppointmentType;
    } catch (error) {
      console.error('Failed to update appointment type:', error);
      throw error;
    }
  };

  // Helper function to populate forms from appointment data
  const populateFormsFromAppointment = (appointment: AppointmentResponse) => {
    if (appointment.form_rules) {
      const formItems: ExpandableFieldItem[] = appointment.form_rules.map((rule, index) => {
        const offset = rule.offset || '';
        let timing: string;
        try {
          timing = formatAppointmentDuration(offset);
        } catch (error) {
          console.warn(`Invalid offset format for form ${index}:`, offset);
          timing = offset || 'at appointment time';
        }

        // Find matching scheduled action
        const scheduledAction = rule.id
          ? findScheduledActionByRuleId(appointment.scheduled_actions, rule.id)
          : undefined;

        return {
          id: `form-${index}`,
          name: rule.title || rule.form_definition_id || `Form ${index + 1}`,
          timing: timing,
          offset: offset,
          formId: rule.form_definition_id,
          ruleId: rule.id,
          status: scheduledAction
            ? mapScheduledActionStatus(scheduledAction.status, 'appointment_form')
            : 'configured',
          scheduledActionStatus: scheduledAction?.status,
          errorMessage: scheduledAction?.error_message || undefined,
          executedAt: scheduledAction?.executed_at || undefined,
          scheduledAt: scheduledAction?.scheduled_at,
          scheduledFor: scheduledAction?.scheduled_at
            ? formatActionTimestamp(scheduledAction.scheduled_at)
            : undefined,
        };
      });
      setSelectedForms(formItems);
    } else {
      setSelectedForms([]);
    }
  };

  // Helper function to populate reminders from appointment data
  const populateRemindersFromAppointment = (appointment: AppointmentResponse) => {
    if (appointment.reminder_rules) {
      const reminderItems: ExpandableFieldItem[] = appointment.reminder_rules.map((rule, index) => {
        const offset = rule.offset || '';
        let timing: string;
        try {
          timing = formatAppointmentDuration(offset);
        } catch (error) {
          console.warn(`Invalid offset format for reminder ${index}:`, offset);
          timing = offset || 'at appointment time';
        }

        // Find matching scheduled action
        const scheduledAction = rule.id
          ? findScheduledActionByRuleId(appointment.scheduled_actions, rule.id)
          : undefined;

        return {
          id: `reminder-${index}`,
          name: rule.title || rule.reminder_template_id || `Reminder ${index + 1}`,
          timing: timing,
          offset: offset,
          ruleId: rule.id,
          status: scheduledAction
            ? mapScheduledActionStatus(scheduledAction.status, 'appointment_reminder')
            : 'configured',
          scheduledActionStatus: scheduledAction?.status,
          errorMessage: scheduledAction?.error_message || undefined,
          executedAt: scheduledAction?.executed_at || undefined,
          scheduledAt: scheduledAction?.scheduled_at,
          scheduledFor: scheduledAction?.scheduled_at
            ? formatActionTimestamp(scheduledAction.scheduled_at)
            : undefined,
        };
      });
      setSelectedReminders(reminderItems);
    } else {
      setSelectedReminders([]);
    }
  };

  // Reset to appointment type defaults
  const resetToDefaults = (appointmentType?: AppointmentType) => {
    const typeToUse = appointmentType || selectedAppointmentType;
    if (!typeToUse) return;

    populateFormsFromDefaults(typeToUse);
    populateRemindersFromDefaults(typeToUse);
  };

  return {
    // State
    selectedForms,
    selectedReminders,
    selectedAppointmentType,

    // Handlers
    handleAddForm,
    handleUpdateForm,
    handleRemoveForm,
    handleAddReminder,
    handleUpdateReminder,
    handleRemoveReminder,
    handleAppointmentTypeSelect,
    handleUpdateAppointmentType,

    // Utilities
    resetToDefaults,
    populateFormsFromDefaults,
    populateRemindersFromDefaults,
    populateFormsFromAppointment,
    populateRemindersFromAppointment,

    // For external access
    getCurrentFormRules: () => selectedForms,
    getCurrentReminderRules: () => selectedReminders,
  };
};

export type UseFormsAndRemindersReturn = ReturnType<typeof useFormsAndReminders>;
